import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import type { ReactNode } from "react";

import { ApiError } from "@/api/errors";
import { AuthProvider } from "@/auth/AuthProvider";
import { ADMIN, STAFF, TENANT } from "@/auth/roles";
import { LoginPage } from "@/features/auth/LoginPage";
import { ErrorBoundary, Loading, ToastProvider } from "@/ui";

import { AppShell } from "./AppShell";
import { RequireAuth, RequireRole } from "./guards";
import { movedRoutes } from "./moved";
import { NotFound } from "./NotFound";

// Each feature is its own chunk: people download only the screens their role can open.
// Sign-up loads the common-password list, so it is a chunk of its own, like the screens.
const SignUpPage = lazy(() => import("@/features/auth/SignUpPage").then((m) => ({ default: m.SignUpPage })));
const ForgotPasswordPage = lazy(() => import("@/features/auth/ForgotPasswordPage").then((m) => ({ default: m.ForgotPasswordPage })));
const InvitePage = lazy(() => import("@/features/auth/InvitePage").then((m) => ({ default: m.InvitePage })));
const ResetPasswordPage = lazy(() => import("@/features/auth/ResetPasswordPage").then((m) => ({ default: m.ResetPasswordPage })));
const LandingPage = lazy(() => import("@/features/landing/LandingPage").then((m) => ({ default: m.LandingPage })));
const OverviewPage = lazy(() => import("@/features/overview/OverviewPage").then((m) => ({ default: m.OverviewPage })));
const LivePage = lazy(() => import("@/features/live/LivePage").then((m) => ({ default: m.LivePage })));
const ReportsPage = lazy(() => import("@/features/reports/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const PeoplePage = lazy(() => import("@/features/people/PeoplePage").then((m) => ({ default: m.PeoplePage })));
const PersonPage = lazy(() => import("@/features/people/PersonPage").then((m) => ({ default: m.PersonPage })));
const TeamsPage = lazy(() => import("@/features/teams/TeamsPage").then((m) => ({ default: m.TeamsPage })));
const TeamPage = lazy(() => import("@/features/teams/TeamPage").then((m) => ({ default: m.TeamPage })));
const RolesPage = lazy(() => import("@/features/roles/RolesPage").then((m) => ({ default: m.RolesPage })));
const StatusesPage = lazy(() => import("@/features/statuses/StatusesPage").then((m) => ({ default: m.StatusesPage })));
const AgentPage = lazy(() => import("@/features/agent/AgentPage").then((m) => ({ default: m.AgentPage })));
const DevicesPage = lazy(() => import("@/features/devices/DevicesPage").then((m) => ({ default: m.DevicesPage })));
const ConnectPage = lazy(() => import("@/features/devices/ConnectPage").then((m) => ({ default: m.ConnectPage })));
const PoliciesPage = lazy(() => import("@/features/policies/PoliciesPage").then((m) => ({ default: m.PoliciesPage })));
const OrganisationPage = lazy(() => import("@/features/organisation/OrganisationPage").then((m) => ({ default: m.OrganisationPage })));
const TenantsPage = lazy(() => import("@/features/tenants/TenantsPage").then((m) => ({ default: m.TenantsPage })));
const PlansPage = lazy(() => import("@/features/plans/PlansPage").then((m) => ({ default: m.PlansPage })));
const SuspendedPage = lazy(() => import("@/features/suspended/SuspendedPage").then((m) => ({ default: m.SuspendedPage })));
const TenantPage = lazy(() => import("@/features/tenants/TenantPage").then((m) => ({ default: m.TenantPage })));
const ProfilePage = lazy(() => import("@/features/profile/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const MyDayPage = lazy(() => import("@/features/myday/MyDayPage").then((m) => ({ default: m.MyDayPage })));
const ScreenshotsPage = lazy(() => import("@/features/screenshots/ScreenshotsPage").then((m) => ({ default: m.ScreenshotsPage })));
const RemovalRequestsPage = lazy(() => import("@/features/screenshots/RemovalRequestsPage").then((m) => ({ default: m.RemovalRequestsPage })));
const MyScreenshotsPage = lazy(() => import("@/features/screenshots/MyScreenshotsPage").then((m) => ({ default: m.MyScreenshotsPage })));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      // Retry network trouble and server errors, never a "no" from the API (4xx).
      retry: (failures, error) => failures < 2 && !(error instanceof ApiError && error.status >= 400 && error.status < 500),
    },
  },
});

/** A screen that fails (often a page that cannot download after an update) shows a way out, not a blank page. */
function RouteErrors({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname} fullscreen>{children}</ErrorBoundary>;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ToastProvider>
          <BrowserRouter>
            <RouteErrors>
              <Suspense fallback={<Loading fullscreen />}>
                <Routes>
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignUpPage />} />
                  <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                  <Route path="/reset-password" element={<ResetPasswordPage />} />
                  <Route path="/invite" element={<InvitePage />} />
                  <Route element={<RequireAuth />}>
                    <Route element={<AppShell />}>
                      <Route path="/organisations" element={<RequireRole roles={["super_admin"]}><TenantsPage /></RequireRole>} />
                      <Route path="/plans" element={<RequireRole roles={["super_admin"]}><PlansPage /></RequireRole>} />
                      <Route path="/suspended" element={<RequireRole roles={["super_admin"]}><SuspendedPage /></RequireRole>} />
                      <Route path="/organisations/:tenantId" element={<RequireRole roles={["super_admin"]}><TenantPage /></RequireRole>} />
                      <Route path="/overview" element={<RequireRole roles={STAFF} reach="others"><OverviewPage /></RequireRole>} />
                      <Route path="/live" element={<RequireRole roles={STAFF} reach="others"><LivePage /></RequireRole>} />
                      <Route path="/reports" element={<RequireRole roles={STAFF} reach="others"><ReportsPage /></RequireRole>} />
                      <Route path="/people" element={<RequireRole roles={STAFF} reach="others"><PeoplePage /></RequireRole>} />
                      <Route path="/people/:userId" element={<RequireRole roles={STAFF} reach="others"><PersonPage /></RequireRole>} />
                      <Route path="/teams" element={<RequireRole roles={STAFF} reach="others"><TeamsPage /></RequireRole>} />
                      <Route path="/teams/:teamId" element={<RequireRole roles={STAFF} reach="others"><TeamPage /></RequireRole>} />
                      <Route path="/roles" element={<RequireRole roles={ADMIN}><RolesPage /></RequireRole>} />
                      <Route path="/screenshots" element={<RequireRole roles={STAFF} reach="screenshots"><ScreenshotsPage /></RequireRole>} />
                      <Route path="/screenshots/removal-requests" element={<RequireRole roles={["tenant_admin"]}><RemovalRequestsPage /></RequireRole>} />
                      <Route path="/my-screenshots" element={<RequireRole roles={TENANT}><MyScreenshotsPage /></RequireRole>} />
                      <Route path="/devices" element={<RequireRole roles={ADMIN} reach="devices"><DevicesPage /></RequireRole>} />
                      <Route path="/connect" element={<RequireRole roles={TENANT}><ConnectPage /></RequireRole>} />
                      <Route path="/agent" element={<RequireRole roles={ADMIN}><AgentPage /></RequireRole>} />
                      {movedRoutes()}
                      <Route path="/statuses" element={<RequireRole roles={ADMIN}><StatusesPage /></RequireRole>} />
                      <Route path="/policies" element={<RequireRole roles={ADMIN}><PoliciesPage /></RequireRole>} />
                      <Route path="/organisation" element={<RequireRole roles={ADMIN}><OrganisationPage /></RequireRole>} />
                      <Route path="/my-day" element={<RequireRole roles={TENANT}><MyDayPage /></RequireRole>} />
                      <Route path="/profile" element={<ProfilePage />} />
                      <Route path="*" element={<NotFound />} />
                    </Route>
                  </Route>
                </Routes>
              </Suspense>
            </RouteErrors>
          </BrowserRouter>
        </ToastProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
